import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { createShareLink, revokeShareLink, updateShareLink } from '../../api/shareLinkApi';
import { Badge } from '../../components/ui/Badge';
import { Button, ButtonRow } from '../../components/ui/Button';
import { GroupedInput, InputGroup, TextField } from '../../components/ui/Field';
import { Callout } from '../../components/ui/Panel';
import { Modal } from '../../components/ui/Modal';
import { formatDateTime } from '../../lib/format';
import { keys } from '../../lib/queryKeys';
import { useConfirm } from '../../providers/ConfirmProvider';
import { useToast } from '../../providers/ToastProvider';
import {
  SHARE_LINK_STATUS,
  SHAREABLE_FIELD_LABELS,
  SHAREABLE_FIELDS,
  buildShareUrl,
  copyToClipboard,
  deriveShareLinkStatus,
  formToTemplateConfig,
  fromDateTimeLocalValue,
  minimumExpiryInputValue,
  shareLinkStatusBadge,
  toDateTimeLocalValue
} from './shareTemplate';

export function StatusBadge({ status }) {
  const badge = shareLinkStatusBadge(status);
  return (
    <Badge tone={badge.tone} icon={badge.icon}>
      {badge.label}
    </Badge>
  );
}

/**
 * "Generate Link": snapshot the composer's shareable fields into a reusable link.
 *
 * Two phases in one dialog. Before creation it explains exactly what is and is not
 * shared; after creation it shows the URL (once — only its hash is stored), the status,
 * an editable expiry and Revoke.
 */
export function ShareLinkModal({ open, ...rest }) {
  // Mounted only while open, so every opening starts fresh and a previous link's
  // one-time URL can never linger.
  return open ? <ShareLinkDialog {...rest} /> : null;
}

function ShareLinkDialog({ onClose, form, onManageLinks }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  /** The link created in this dialog session, including its one-time URL. */
  const [link, setLink] = useState(null);
  const [linkExpiry, setLinkExpiry] = useState('');

  // Snapshot taken when the dialog opens: what is reviewed is exactly what is shared.
  const [config] = useState(() => formToTemplateConfig(form));

  const filledFields = SHAREABLE_FIELDS.filter((field) => {
    const value = config[field];
    return typeof value === 'boolean' ? value : String(value ?? '').trim() !== '';
  });

  const canShare = String(config.subject ?? '').trim() !== '' || String(config.message ?? '').trim() !== '';
  const minExpiry = minimumExpiryInputValue();
  const status = link ? deriveShareLinkStatus(link) : null;
  const url = link?.path ? buildShareUrl(link.path, window.location.origin) : '';

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: keys.shareLinks });

  async function handleCreate() {
    setError('');
    if (!canShare) {
      setError('Add a subject or message before generating a link.');
      return;
    }
    setIsCreating(true);
    try {
      const created = await createShareLink({
        config,
        name: name.trim() || undefined,
        expiresAt: fromDateTimeLocalValue(expiry)
      });
      setLink(created);
      setLinkExpiry(toDateTimeLocalValue(created.expiresAt));
      invalidateList();
    } catch (err) {
      setError(err.message || 'Could not create the link.');
    } finally {
      setIsCreating(false);
    }
  }

  async function handleCopy() {
    if (await copyToClipboard(url)) toast.success('Link copied to clipboard.');
    else toast.error('Could not copy automatically — select the link and copy it manually.');
  }

  async function handleSaveExpiry() {
    if (!link) return;
    setIsSaving(true);
    try {
      const updated = await updateShareLink(link.id, { expiresAt: fromDateTimeLocalValue(linkExpiry) });
      setLink((current) => ({ ...current, ...updated }));
      invalidateList();
      toast.success(updated.expiresAt ? `Link now expires ${formatDateTime(updated.expiresAt)}.` : 'Link no longer expires.');
    } catch (err) {
      toast.error(err.message || 'Could not update the expiry.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRevoke() {
    if (!link) return;
    const ok = await confirm({
      title: 'Revoke this link?',
      message: [
        'Anyone opening it will see that it has been revoked.',
        'Forms already opened from it are unaffected. This cannot be undone.'
      ],
      confirmLabel: 'Revoke Link',
      cancelLabel: 'Keep Link',
      tone: 'danger'
    });
    if (!ok) return;

    setIsRevoking(true);
    try {
      const updated = await revokeShareLink(link.id);
      setLink((current) => ({ ...current, ...updated }));
      invalidateList();
      toast.success('Link revoked.');
    } catch (err) {
      toast.error(err.message || 'Could not revoke the link.');
    } finally {
      setIsRevoking(false);
    }
  }

  const footer = (
    <ButtonRow className="justify-end">
      {onManageLinks ? (
        <Button variant="ghost" icon="fa-list" onClick={onManageLinks}>
          Manage links
        </Button>
      ) : null}
      {link ? (
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      ) : (
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="fa-link"
            onClick={handleCreate}
            loading={isCreating}
            loadingLabel="Generating…"
            disabled={!canShare}
          >
            Generate Link
          </Button>
        </>
      )}
    </ButtonRow>
  );

  return (
    <Modal open onClose={onClose} title="Share campaign form" titleIcon="fa-link" size="lg" footer={footer}>
      {!link ? (
        <div className="flex flex-col gap-4">
          <p className="text-base text-muted">
            Creates a reusable link to a copy of this form. Anyone with the link can view the settings and
            open them in their own composer. Their edits never change your form or the link. Opening a link
            never sends email.
          </p>

          <Callout tone="warning" icon="fa-triangle-exclamation">
            The link is public: anyone who has it can see the values below, including the SMTP host and
            username. The SMTP password, recipient files and test recipients are <strong>never</strong> shared.
          </Callout>

          <TextField
            label="Link name (optional)"
            placeholder={config.subject ? `Defaults to "${config.subject.slice(0, 60)}"` : 'e.g. Spring promo template'}
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
          />

          <TextField
            label="Expires (optional)"
            type="datetime-local"
            hint="Leave empty for a link that never expires. You can change this later."
            value={expiry}
            min={minExpiry}
            onChange={(event) => setExpiry(event.target.value)}
          />

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-ink-700">Fields that will be shared</span>
            {filledFields.length ? (
              <div className="flex flex-wrap gap-1.5">
                {filledFields.map((field) => (
                  <Badge key={field} tone="neutral" showIcon={false}>
                    {SHAREABLE_FIELD_LABELS[field]}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-sm text-muted">Nothing filled in yet.</span>
            )}
          </div>

          {error ? (
            <p className="text-sm text-danger-600" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-ink-700">Status</span>
            <StatusBadge status={status} />
            {link.name ? <span className="text-sm text-muted">· {link.name}</span> : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="share-link-url" className="text-sm font-semibold text-ink-700">
              Shareable URL
            </label>
            <InputGroup>
              <GroupedInput
                id="share-link-url"
                readOnly
                value={url}
                className="font-mono text-sm"
                onFocus={(event) => event.target.select()}
              />
              <Button variant="secondary" icon="fa-copy" onClick={handleCopy} className="rounded-l-none">
                Copy Link
              </Button>
            </InputGroup>
            <span className="text-xs text-muted">
              <i className="fa-solid fa-circle-info" aria-hidden="true" /> Copy it now. For security the full
              link is not stored and cannot be shown again. You can still change its expiry or revoke it from
              Manage links.
            </span>
          </div>

          {status !== SHARE_LINK_STATUS.REVOKED ? (
            <div className="flex flex-wrap items-end gap-2">
              <TextField
                className="min-w-[14rem] flex-1"
                label="Expires"
                type="datetime-local"
                hint="Empty means the link never expires."
                value={linkExpiry}
                min={minExpiry}
                onChange={(event) => setLinkExpiry(event.target.value)}
              />
              <Button variant="secondary" icon="fa-floppy-disk" onClick={handleSaveExpiry} loading={isSaving}>
                Save expiry
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted">
              Revoked {formatDateTime(link.revokedAt)}. The link no longer opens.
            </p>
          )}

          {status !== SHARE_LINK_STATUS.REVOKED ? (
            <div>
              <Button variant="danger" icon="fa-ban" onClick={handleRevoke} loading={isRevoking} loadingLabel="Revoking…">
                Revoke Link
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
